import { nanoid } from 'nanoid';
import { invokeFunction, supabase } from '@/integrations/supabase/client';
import { CompanyData } from '@/types';
import { TEST_NAME_PREFIX } from '@/shared/merchantStatus';

// "Auto fill Test Client" for Admin › Add Merchant (FIG-319 / REQ-037): fake
// company and contact data, the [TEST] prefix, and an EIN that no existing
// merchant uses.

const NAME_FIRST = ['Juniper', 'Copper', 'Willow', 'Saltmarsh', 'Ember', 'Harbor', 'Linden', 'Marigold', 'Slate', 'Thistle'];
const NAME_SECOND = ['Goods', 'Supply Co.', 'Apparel', 'Home', 'Outfitters', 'Trading', 'Kitchen', 'Botanicals'];
const STATES = ['DE', 'CA', 'NY', 'TX'];
const TEST_ADDRESS = '123 Test Street, Test City, TS 12345';
const MAX_EIN_ATTEMPTS = 5;

type Random = () => number;

const pick = <T>(items: readonly T[], random: Random): T => items[Math.floor(random() * items.length)];

export function randomEin(random: Random = Math.random): string {
  const prefix = 10 + Math.floor(random() * 90);
  const serial = String(Math.floor(random() * 10_000_000)).padStart(7, '0');
  return `${prefix}-${serial}`;
}

export function buildTestClientData(ein: string, random: Random = Math.random): CompanyData {
  const tag = Math.floor(random() * 36 ** 5).toString(36).padStart(5, '0');
  return {
    name: `${TEST_NAME_PREFIX} ${pick(NAME_FIRST, random)} ${pick(NAME_SECOND, random)}`,
    name_dbas: '',
    address: TEST_ADDRESS,
    stateOfIncorporation: pick(STATES, random),
    ein,
    revenueLast12Months: 100_000 + Math.floor(random() * 4_900_000),
    pocName: `Test Contact ${tag.toUpperCase()}`,
    pocEmail: `test+${tag}@test.local`,
    pocPhone: '',
    pocPhoneVerified: false,
    products: [{ id: nanoid(), name: '', description: '', category: '' }],
    terms: false,
  };
}

async function isEinInUse(ein: string): Promise<boolean> {
  const { data, error } = await invokeFunction(supabase, 'validate-ein-duplicate-v2', { body: { ein } });
  if (error) throw error;
  return Boolean((data as { isDuplicate?: boolean } | null)?.isDuplicate);
}

/** Fake data with an EIN checked against all existing merchants. */
export async function generateTestClient(random: Random = Math.random): Promise<CompanyData> {
  for (let attempt = 0; attempt < MAX_EIN_ATTEMPTS; attempt += 1) {
    const ein = randomEin(random);
    if (!(await isEinInUse(ein))) return buildTestClientData(ein, random);
  }
  throw new Error('Could not find an unused EIN for the test client');
}
